import React from 'react';

import { useSelector } from 'react-redux';

import { useQuery } from '@tanstack/react-query';
import { addMonths, parseISO, subMonths } from 'date-fns';
import { isEqual } from 'lodash-es';

import { useFeature } from '@asteria/component-tools/featureflag';

import * as ForecasterStore from '@asteria/datalayer/stores/forecaster';
import * as ScenarioStore from '@asteria/datalayer/stores/scenarios';

import { useRequestLoader } from '@asteria/utils-hooks/useDataLoader';

import { formatResponse, validateFormValues } from '../../utils';

export function useForecastQuery({ onSubmit }) {
	const hasOverdueFeature = useFeature('forecaster-status-overdue');

	const currentDate = useSelector(ForecasterStore.selectors.navigation.date);

	const dataLoader = useRequestLoader({ onSubmit });

	const date = React.useMemo(
		() => (currentDate ? parseISO(currentDate) : new Date()),
		[currentDate],
	);

	const sourceScenarioData = useSelector(
		ScenarioStore.selectors.sourceScenarioData,
		isEqual,
	);

	const query = useQuery({
		queryKey: ['forecaster', 'home', sourceScenarioData?.name, date],
		queryFn: async () => {
			const status = ['UNPAID', 'SIGNED'];

			if (hasOverdueFeature) {
				status.push('OVERDUE');
			}

			return await onSubmit?.('forecast:fetch', {
				startDate: subMonths(date, 2),
				endDate: addMonths(date, 2),
				scenarioName: sourceScenarioData?.name,
				dataLoader: dataLoader,
				history: {
					fields: `
								date
								types {
									type
									total
									currency
									statuses {
										status
										total
										currency
									}
								}
							`,
				},
				current: {
					fields: `
					nodes {
						date
						types {
							type
							total
							probability
							currency
							categories {
								_id
								details {
									name
									color
								}
								total
								probability
								currency
								tags {
									_id
									details {
										name
										color
									}
									total
									probability
									currency
								}
							}
						}
					}
					`,
				},
				status,
			});
		},
		keepPreviousData: true,
		refetchOnMount: true,
		refetchOnReconnect: false,
		refetchOnWindowFocus: false,
		placeholderData: null,
	});

	const formattedHistory = query?.data
		? formatResponse(query?.data?.history)
		: {};

	const currentData = query?.data
		? formatResponse(query?.data?.current)
		: null;

	const validated = currentData
		? validateFormValues(currentData, sourceScenarioData)
		: {};

	return {
		query,
		form: { ...validated, extra: { history: formattedHistory } },
	};
}

export function useForecastActionsQuery({ onSubmit }) {
	const sourceScenario = useSelector(
		ScenarioStore.selectors.scenario,
		isEqual,
	);

	const response = useQuery({
		queryKey: ['forecaster', 'actions', sourceScenario],
		queryFn: async () => onSubmit?.('forecast:actions:fetch'),
		refetchOnMount: true,
		refetchOnReconnect: false,
		refetchOnWindowFocus: false,
		placeholderData: null,
		cacheTime: 0,
		staleTime: 0,
	});

	return response;
}
